import { useState, useRef, useEffect, useCallback } from 'react';

const useTimer = ({ initialTime = 0, direction = 'down', onComplete }) => {
  // Initial time will be passed as a timestamp or seconds.
  const [time, setTime] = useState(initialTime);
  const intervalRef = useRef(null);
  const startTimeRef = useRef(null);

  const calculateRemainingTime = useCallback(() => {
    if (direction === 'down') {
      // Get the current time (in seconds).
      const currentTime = Date.now();

      // Calculate the difference between the timestamp and the current time.
      const remainingTime = initialTime - currentTime;

      return remainingTime > 0 ? remainingTime : 0; // Ensure it doesn't go negative.
    } else {
      // For countup, return the initial time directly
      return initialTime;
    }
  }, [initialTime, direction]);

  const startTimer = useCallback(() => {
    // Clear any existing interval.
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // For countup, store the adjusted start time
    if (direction === 'up') {
      // Subtract initialTime from current time to create proper starting point
      startTimeRef.current = Date.now() - initialTime;
    }

    intervalRef.current = setInterval(() => {
      if (direction === 'down') {
        // Countdown logic — recompute from the clock every tick. Decrementing
        // a captured value drifts arbitrarily far behind real time in
        // throttled background tabs.
        const remaining = calculateRemainingTime();
        setTime(remaining);

        // Handle completion for countdown
        if (remaining <= 0) {
          clearInterval(intervalRef.current);
          setTime(0);
          if (onComplete) {
            onComplete();
          }
        }
      } else {
        // Countup logic - calculate elapsed time since start
        const elapsed = Date.now() - startTimeRef.current;
        setTime(elapsed);
      }
    }, 1000);
  }, [direction, calculateRemainingTime, initialTime, onComplete]);

  // Reset timer with new initial time
  const resetTimer = useCallback(
    (newTime) => {
      setTime(newTime);
      startTimer();
    },
    [startTimer]
  );

  // Stop timer
  const stopTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Start timer when initialTime changes
  useEffect(() => {
    if (initialTime !== undefined && !isNaN(initialTime)) {
      setTime(calculateRemainingTime());
      startTimer();
    }
  }, [initialTime, calculateRemainingTime, startTimer]);

  return {
    time,
    resetTimer,
    stopTimer,
  };
};

export default useTimer;
